import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";

const MAX_CHARACTERS = 1000;

/**
 * Extracted from admin/NewClient.jsx - same validation, same payload
 * shape, same endpoint (POST /api/clients).
 */
export function useNewClient() {
  const { t } = useTranslation();
  const initialFormState = {
    company: "",
    product: "",
    address: "",
    urlCalendar: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    secondaryEmail: "",
    preferredLanguage: "",
    script: "",
  };

  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [id, setId] = useState(0);

  const handleChange = (name, value) => {
    if (name === "script") {
      if (value.length <= MAX_CHARACTERS) {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      setCharCount(value.length);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!form.company.trim()) newErrors.company = t("validation.requiredField");
    if (!form.product.trim()) newErrors.product = t("validation.requiredField");
    if (!form.address.trim()) newErrors.address = t("validation.requiredField");
    if (!form.preferredLanguage) newErrors.preferredLanguage = t("validation.requiredLanguage");
    if (!form.urlCalendar.trim()) newErrors.urlCalendar = t("validation.requiredField");
    if (!form.contactPerson.trim()) newErrors.contactPerson = t("validation.requiredField");
    if (!form.contactPhone.trim()) newErrors.contactPhone = t("validation.requiredField");

    if (!form.contactEmail.trim()) {
      newErrors.contactEmail = t("validation.requiredField");
    } else if (!emailRegex.test(form.contactEmail)) {
      newErrors.contactEmail = t("validation.invalidEmail");
    }

    if (form.secondaryEmail.trim()) {
      const secondaryEmails = form.secondaryEmail.split(",").map((e) => e.trim());
      const invalidEmails = secondaryEmails.filter((email) => email !== "" && !emailRegex.test(email));
      if (invalidEmails.length > 0) newErrors.secondaryEmail = t("validation.invalidEmail");
    }

    if (!form.script.trim()) newErrors.script = t("validation.requiredField");
    return newErrors;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrorModal(true);
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }
    const clientData = {
      Address: form.address,
      Contact_Email: form.contactEmail,
      Contact_Person: form.contactPerson,
      Contact_Phone: form.contactPhone,
      Dialog: form.script,
      Name: form.company,
      PreferredLanguage: form.preferredLanguage,
      Product: form.product,
      Url_Calendar: form.urlCalendar,
      EmailNotifications: {
        bcc: [],
        cc: form.secondaryEmail
          ? form.secondaryEmail.split(",").map((email) => email.trim()).filter((email) => email !== "")
          : [],
      },
    };
    const accessToken = await getValidToken();
    const config = { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } };

    try {
      const response = await axios.post(`${process.env.REACT_APP_APIBASEURL}/api/clients`, clientData, config);
      if (response.status === 201 || response.status === 200) {
        setId(response.data.clientId || response.data.Id || response.data.id);
        setSuccessModal(true);
      }
    } catch (error) {
      const serverData = error.response?.data;
      const errorMessage = serverData ? serverData.details || serverData.error || t("error.generic") : t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setSuccessModal(false);
    setForm(initialFormState);
  };

  return {
    form,
    errors,
    charCount,
    isSubmitting,
    successModal,
    errorModal,
    id,
    handleChange,
    handleSubmit,
    closeSuccessModal,
    setErrorModal,
    MAX_CHARACTERS,
  };
}